import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Shield, PenTool, Eye, Edit, MessageCircle, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'read_only',
    status: 'pending',
    departments: [],
    password: '',
    can_send_messages: true,
    can_receive_messages: true,
    email_notifications: true
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const roles = [
    {
      value: 'read_only',
      label: 'Read Only',
      description: 'Can view content but cannot make changes',
      icon: Eye,
      color: 'text-gray-600'
    },
    {
      value: 'contributor',
      label: 'Contributor',
      description: 'Can create new content but cannot edit or delete existing content',
      icon: PenTool,
      color: 'text-blue-600'
    },
    {
      value: 'admin',
      label: 'Full Control',
      description: 'Full access including admin tasks and user management',
      icon: Shield,
      color: 'text-red-600'
    }
  ];

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }
  ];

  const departments = [
    { value: 'product_development', label: 'Product Development' },
    { value: 'product_marketing', label: 'Product Marketing' },
    { value: 'sales', label: 'Sales' }
  ];

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        full_name: user.full_name || '',
        role: user.role || 'read_only',
        status: user.status || 'pending',
        departments: user.departments || [],
        password: '', // Always empty for security
        can_send_messages: user.can_send_messages !== undefined ? user.can_send_messages : true,
        can_receive_messages: user.can_receive_messages !== undefined ? user.can_receive_messages : true,
        email_notifications: user.email_notifications !== undefined ? user.email_notifications : true
      });
    }
  }, [user, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDepartmentChange = (departmentValue, checked) => {
    setFormData(prev => ({
      ...prev,
      departments: checked 
        ? [...prev.departments, departmentValue]
        : prev.departments.filter(d => d !== departmentValue)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    
    setLoading(true);

    try {
      await axios.put(`${API}/users/${user.id}`, formData);
      
      toast({
        title: "Success",
        description: "User updated successfully",
      });
      
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = roles.find(r => r.value === formData.role);

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Edit className="w-5 h-5 text-blue-600" />
            <span>Edit User</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Leave empty to keep current password"
              />
              <p className="text-xs text-slate-500">
                Leave empty if you don't want to change the password
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>User Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => handleInputChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>Permission Level *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center space-x-2">
                        <Icon className={`w-4 h-4 ${role.color}`} />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {selectedRole && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center space-x-2 mb-1">
                  <selectedRole.icon className={`w-4 h-4 ${selectedRole.color}`} />
                  <span className="font-medium text-slate-700">{selectedRole.label}</span>
                </div>
                <p className="text-sm text-slate-600">{selectedRole.description}</p>
              </div>
            )}
          </div>

          {/* Department Access */}
          <div className="space-y-2">
            <Label>Department Access</Label>
            <div className="space-y-3">
              {departments.map((dept) => (
                <div key={dept.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={dept.value}
                    checked={formData.departments.includes(dept.value)}
                    onCheckedChange={(checked) => handleDepartmentChange(dept.value, checked)}
                  />
                  <Label htmlFor={dept.value} className="text-sm font-normal">
                    {dept.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Messaging Permissions */}
          <div className="space-y-2">
            <Label className="flex items-center space-x-2">
              <MessageCircle className="w-4 h-4" />
              <span>Messaging Permissions</span>
            </Label>
            <div className="space-y-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Can Send Messages</Label>
                  <p className="text-xs text-slate-500">
                    Allow user to send messages to other team members
                  </p>
                </div>
                <Checkbox
                  checked={formData.can_send_messages}
                  onCheckedChange={(checked) => handleInputChange('can_send_messages', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Can Receive Messages</Label>
                  <p className="text-xs text-slate-500">
                    Allow user to receive messages from other team members
                  </p>
                </div>
                <Checkbox
                  checked={formData.can_receive_messages}
                  onCheckedChange={(checked) => handleInputChange('can_receive_messages', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <div>
                    <Label className="text-sm font-medium">Email Notifications</Label>
                    <p className="text-xs text-slate-500">
                      Send email alerts when user receives messages
                    </p>
                  </div>
                </div>
                <Checkbox
                  checked={formData.email_notifications}
                  onCheckedChange={(checked) => handleInputChange('email_notifications', checked)}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.username || !formData.email || !formData.full_name}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {loading ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;